<script setup>
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import {useStore} from "vuex";
import {computed, onMounted} from "vue";
import HistoricDataRow from "@/components/backlinks/partials/HistoricDataRow.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {getBacklinks} from "@/composables/useBacklinks";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import LineChart from "@/components/global-themed/charts/LineChart.vue";

const store = useStore();

const orderBy = computed(() => store.getters['backlinks/authorityOrderBy']);
const orderDirection = computed(() => store.getters['backlinks/authorityOrderDirection']);

const {data: authorityData, isLoading} = getBacklinks('authority');

const historicData = computed(() =>
  [...authorityData.value?.['historicData'] ?? []]
    .sort((a, b) => {
      if (orderDirection.value === 'asc') {
        return a[orderBy.value] > b[orderBy.value] ? 1 : -1;
      }
      return a[orderBy.value] < b[orderBy.value] ? 1 : -1;
    })
);

const chartData = computed(() => authorityData.value['chartData']);

const changeOrderBy = (name) => {
  store.dispatch('backlinks/setAuthorityOrderBy', name);
}

const xAxis = computed(() => {
  return (chartData.value ?? []).map((row => row.date));
});

const mapValuesToSeries = (values, property) => {
  if (!values) {
    return [];
  }

  return values.map((row, index) => ({
    x: new Date(xAxis.value[index]).getTime(),
    y: row[property],
  }));
}

const series = computed(() => [
    {
      name: 'Domain Authority',
      id: 'domainAuthority',
      data: mapValuesToSeries(chartData.value, 'domainAuthority'),
    },
    {
      name: 'Trust Flow',
      data: mapValuesToSeries(chartData.value, 'trustFlow'),
      visible: false,
    },
    {
      name: 'Citation Flow',
      data: mapValuesToSeries(chartData.value, 'citationFlow'),
      visible: false,
    },
    {
      name: 'External Backlinks',
      data: mapValuesToSeries(chartData.value, 'externalBacklinks'),
    },
    {
      name: 'Referral Domains',
      data: mapValuesToSeries(chartData.value, 'referralDomains'),
    },
]);

const options = {
  tooltip: {
    x: {
      format: 'dd MMMM yyyy',
    },
  },
  xaxis: {
    tooltip: {
      enabled: false
    },
    type: 'datetime',
    labels: {
      style: {
        cssClass: 'apexcharts-xaxis-label',
      }
    }
  },
  yaxis: [
    {
      min: 0,
      labels: {
        style: {
          cssClass: 'apexcharts-xaxis-label',
        }
      }
    },
    {
      min: 0,
      seriesName: 'Domain Authority',
      opposite: true,
      labels: {
        style: {
          cssClass: 'apexcharts-xaxis-label',
        }
      }
    }
  ],
};

onMounted(() => {
  changeOrderBy('date');
})
</script>

<template>
  <div class="tw-py-8">
    <h3 class="tw-text-2xl tw-font-bold tw-font-sans tw-text-grey-dark tw-mb-6">
      Authority and Link Metrics Over Time
    </h3>

    <div
      v-if="!isLoading"
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-mt-16 tw-mb-8"
    >
      <LineChart
        :series="series"
        :options="options"
      />
    </div>
    <div
      v-if="isLoading"
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-w-full tw-mb-8"
    >
      <LoadingBlock />
    </div>
  </div>

  <DataTable>
    <TableHeaderRow class="tw-grid-cols-12">
      <TableHeaderCell
        col-span="2"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="date"
        @order-changed="changeOrderBy"
      >
        <span>Date</span>
      </TableHeaderCell>

      <TableHeaderCell
        col-span="2"
        class="tw-justify-center tw-text-center tw-px-1"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="domainAuthority"
        @order-changed="changeOrderBy"
      >
        <span
          v-tooltip="{
            content: `Domain Authority is a metric that is unique to us, we use our own algorithms<br/>to calculate an overall score based on a combination of your trust flow and citation flow.`,
            html: true,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
        >Domain Authority</span>
      </TableHeaderCell>

      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="trustFlow"
        col-span="2"
        class="tw-justify-center tw-text-center tw-px-1"
        @order-changed="changeOrderBy"
      >
        <span
          v-tooltip="{
            content: `Trust Flow is a metric that analyses <b>how trustworthy a site is by measuring its quality</b>.<br/>Quality is the essential data of this metric. If a link pointing to your website is authoritative and qualitative,<br />then your trust flow will increase.`,
            html: true,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
        >Trust Flow</span>
      </TableHeaderCell>

      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="citationFlow"
        col-span="2"
        class="tw-justify-center tw-text-center tw-px-1"
        @order-changed="changeOrderBy"
      >
        <span
          v-tooltip="{
            content: `Citation Flow is a metric that refers to <b>the popularity of a link</b> in a site without considering the quality of these links.<br />The perfect example is gambling website sites. They have a very high Citation Flow but these links are often not qualitative.<br />A website with a lot of links pointing to it will be influential and will get a good citation flow.`,
            html: true,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
        >Citation Flow</span>
      </TableHeaderCell>

      <TableHeaderCell
        col-span="2"
        class="tw-justify-center tw-text-center tw-px-1"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="externalBacklinks"
        @order-changed="changeOrderBy"
      >
        <span
          v-tooltip="{
            content: `External Backlinks is a count of the number of backlinks that point to your website as a whole.<br />Unlike referring domains if you have 2 backlinks from <a href='https://uk.yahoo.com' target='_blank'>yahoo.com</a> one on the homepage and one on an internal page, this will be counted as 2 external backlinks.`,
            html: true,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
        >
          External Backlinks
        </span>
      </TableHeaderCell>
      <TableHeaderCell
        :order-by="orderBy"
        class="tw-justify-center tw-text-center tw-px-1"
        :order-direction="orderDirection"
        name="referralDomains"
        col-span="2"
        @order-changed="changeOrderBy"
      >
        <span
          v-tooltip="{
            content: `Referring domains is a count of the number of unique website domains that point to your website.<br />If you have 2 backlinks from <a href='https://uk.yahoo.com' target='_blank'>yahoo.com</a> one on the homepage and one on an internal page, this will be counted as 1 referring domain.<br />That domain would be <a href='https://uk.yahoo.com' target='_blank'>yahoo.com</a>.`,
            html: true,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
        >Referral Domains</span>
      </TableHeaderCell>
    </TableHeaderRow>

    <TableBody
      v-if="historicData.length > 0"
      class="tw-max-h-[70vh]"
    >
      <HistoricDataRow
        v-for="data in historicData"
        :key="data.id"
        :historic-data="data"
      />
    </TableBody>

    <ZeroContent v-if="!historicData.length">
      Looks like there are no backlinks to display.
    </ZeroContent>
  </DataTable>
</template>
